import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Droplets,
  Wind,
  ShieldAlert,
  CheckCircle2,
  Layers,
  ArrowRight,
  ArrowLeft,
  ShoppingBag,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import ContentPage from "./ContentPage";
import { updateSEO } from "../utils/seo";
import "./CareGuide.css";

export default function CareGuide() {
  const { isRTL, language } = useLanguage();

  useEffect(() => {
    updateSEO({
      title: isRTL
        ? "دليل العناية بالمنسوجات | غسيل الفوط والبرانص والمايكروفايبر والقطن — تيري لاند"
        : "Textile Care Guide | Washing Towels, Robes, Microfiber & Cotton — Terry Land",
      description: isRTL
        ? "إرشادات الخبراء للعناية بأرواب وبرانص الحمام، الفوط، والبشاكير بخامات القطن والمايكروفايبر والوجهين لمنع روائح الرطوبة والكمكمة والحفاظ على النعومة الدائمة."
        : "Expert laundering instructions for bathrobes, towels, and bath sheets in microfiber, cotton, and dual-face weaves to preserve softness and absorbency.",
      canonical: "https://terryland.net/care",
    });
  }, [isRTL, language]);

  return (
    <ContentPage
      title={isRTL ? "دليل العناية بالمنسوجات" : "Fine Textile Care Guide"}
      eyebrow={isRTL ? "إرشادات الخبراء والاستدامة" : "Longevity & Care"}
    >
      {/* Lead Paragraph */}
      <p className="lead care-lead">
        {isRTL
          ? "العناية المدروسة تحافظ على نعومة منسوجات تيري لاند وقدرتها الفائقة على الامتصاص لسنوات طويلة. صُممت إرشاداتنا لتلائم كل خامة من خاماتنا الثلاث وتضمن لك تجربة فندقية فاخرة في كل استخدام."
          : "Thoughtful care keeps Terry Land pieces exceptionally soft, plush, and absorbent for years. Our guidelines are tailored to each of our three signature fabrics to preserve their five-star luxury wash after wash."}
      </p>

      {/* ── Section: Materials & Care ── */}
      <section className="care-section">
        <h2>
          {isRTL
            ? "العناية حسب نوع الخامة"
            : "Fabric-Specific Care Instructions"}
        </h2>
        <p className="care-section-sub">
          {isRTL
            ? "لكل خامة طبيعتها وخصائصها الفريدة، واتباع طريقة العناية المناسبة يمنحك أفضل أداء ونعومة تدوم:"
            : "Each fabric possesses distinct characteristics. Following the right care method ensures optimal absorbency, softness, and longevity:"}
        </p>

        <div className="care-fabrics-grid">
          {/* 1. Cotton on Both Sides */}
          <div className="care-fabric-card">
            <div className="care-fabric-card__header">
              <span className="care-fabric-badge">
                {isRTL ? "الخامة الأولى" : "Fabric 1"}
              </span>
              <h3>
                {isRTL ? "قطن على الوجهين" : "Cotton on Both Sides"}
              </h3>
            </div>
            <p className="care-fabric-card__desc">
              {isRTL
                ? "خامة ناعمة وعالية الامتصاص ومثالية لمن يفضلون الملمس الطبيعي للقطن. نظراً لأن القطن بطبيعته خامة أثقل وزناً وتحتفظ بالماء داخل أليافها، فإنه يتطلب تجفيفاً ونشراً جيداً بعد كل استخدام؛ حيث إن ترك المنشفة القطنية مبللة دون نشرها وفردها لتجف يؤدي سريعاً إلى ظهور رائحة كمكمة وعفن غير محببة وتكاثر البكتيريا عليها."
                : "Soft, highly absorbent, and ideal for those who cherish the natural touch of cotton. Because natural cotton is heavier and holds moisture deeply within its fibers, it requires proper drying and prompt hanging after each use; leaving a wet cotton towel bunched up without hanging it to air-dry will cause musty, moldy odors and bacterial growth."}
            </p>
            <div className="care-fabric-card__tip">
              <ShieldAlert size={16} />
              <span>
                {isRTL
                  ? "القاعدة الذهبية: انشر المنشفة مفرودة تماماً على حامل عريض في مكان جيد التهوية فور الاستحمام."
                  : "Golden Rule: Always hang unfolded on a wide bar in a well-ventilated space immediately after use."}
              </span>
            </div>
          </div>

          {/* 2. 100% Microfiber */}
          <div className="care-fabric-card">
            <div className="care-fabric-card__header">
              <span className="care-fabric-badge care-fabric-badge--micro">
                {isRTL ? "الخامة الثانية" : "Fabric 2"}
              </span>
              <h3>
                {isRTL ? "مايكروفايبر 100%" : "100% Microfiber"}
              </h3>
            </div>
            <p className="care-fabric-card__desc">
              {isRTL
                ? "خامة فائقة النعومة، خفيفة الوزن بوزن يبلغ حوالي 320 GSM، وسريعة الجفاف بصورة استثنائية. تتميز بأنها مضادة للبكتيريا والجراثيم بشكل طبيعي، ولا تحتفظ بالروائح الكريهة، مما يجعلها الخيار الأسهل في العناية والأسرع في الغسيل والتجفيف."
                : "Ultra-soft, lightweight at approximately 320 GSM, and exceptionally fast-drying. Naturally antibacterial and resistant to damp odors, making it the easiest fabric to maintain and the fastest to wash and dry."}
            </p>
            <div className="care-fabric-card__tip care-fabric-card__tip--good">
              <CheckCircle2 size={16} />
              <span>
                {isRTL
                  ? "مثالية لشهر العسل، حقائب السفر، النادي الرياضي، وتجفيف شعر العروس برفق ودون هيشان."
                  : "Perfect for honeymoons, travel bags, gym workouts, and frictionless bridal hair drying."}
              </span>
            </div>
          </div>

          {/* 3. Dual-Face Cotton + Microfiber */}
          <div className="care-fabric-card">
            <div className="care-fabric-card__header">
              <span className="care-fabric-badge care-fabric-badge--dual">
                {isRTL ? "الخامة الثالثة (المزدوجة)" : "Fabric 3 (Dual-Face)"}
              </span>
              <h3>
                {isRTL
                  ? "خامة الوجهين (قطن + مايكروفايبر)"
                  : "Dual-Face Cotton + Microfiber"}
              </h3>
            </div>
            <p className="care-fabric-card__desc">
              {isRTL
                ? "الابتكار الذي يجمع أفضل ما في الاثنين: وجه قطني ملامس للبشرة يمنحك الملمس الطبيعي الفاخر وقوة الامتصاص، بينما صُنع الوجه الآخر من المايكروفايبر ليمنح المنشفة النعومة والخفة وسرعة الجفاف. تمنحك هذه الخامة دلال القطن على الجلد وفي نفس الوقت سهولة وسرعة جفاف المايكروفايبر ومقاومة الانكماش."
                : "Our signature innovation uniting the privileges of both worlds: pure cotton loops against the skin for a natural, highly absorbent feel, paired with plush microfiber on the outer side for softness, lightness, and accelerated drying that resists shrinkage and prevents odors."}
            </p>
            <div className="care-fabric-card__tip care-fabric-card__tip--dual">
              <Layers size={16} />
              <span>
                {isRTL
                  ? "الخيار الأفضل لأرواب الكابلز ومناشف الحمام الرئيسية في بيت الزوجية الجديد."
                  : "The ultimate choice for couple bathrobes and master ensuite linens in the marital home."}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section: Core Washing & Laundry Masterclass ── */}
      <section className="care-section">
        <h2>
          {isRTL
            ? "قواعد الغسيل والكي الأساسية"
            : "The Golden Laundering Rules"}
        </h2>

        <div className="care-rules-list">
          {/* Washing Rule (User's Exact Specification) */}
          <div className="care-rule-item">
            <div className="care-rule-icon">
              <Droplets size={22} />
            </div>
            <div className="care-rule-content">
              <h4>
                {isRTL ? "دورة الغسيل والحرارة" : "Wash Cycle & Temperature"}
              </h4>
              <p>
                {isRTL
                  ? "يُفضل الغسيل في الغسالة الأوتوماتيكية على دورة غسيل معتدلة بدرجة حرارة 30–40 درجة مئوية مع ألوان مماثلة، باستخدام منظف سائل لطيف. يُنصح بتجنب درجات الحرارة المرتفعة والمنظفات القوية، وعدم استخدام المبيضات إلا إذا كانت مناسبة للخامة."
                  : "It is best to machine wash on a gentle cycle at 30–40°C with similar colors, using a mild liquid detergent. We recommend avoiding high temperatures and harsh detergents, and refraining from using bleach unless it is specifically suitable for the fabric."}
              </p>
            </div>
          </div>

          {/* Softener & Vinegar Rule */}
          <div className="care-rule-item">
            <div className="care-rule-icon">
              <Sparkles size={22} />
            </div>
            <div className="care-rule-content">
              <h4>
                {isRTL
                  ? "تجنب المنعمات الكيميائية والاعتماد على الخل"
                  : "Skip Fabric Softeners; Use White Vinegar"}
              </h4>
              <p>
                {isRTL
                  ? "تجنب تماماً استخدام منعمات الأقمشة التجارية على المناشف، لأن السيليكون يغلف ألياف القطن ويقلل قدرتها على امتصاص الماء. بدلاً من ذلك، أضف نصف كوب من الخل الأبيض الطبيعي في درج الشطف الأخير لإزالة أي ترسبات كلسية وإعادة النعومة والمرونة للألياف."
                  : "Avoid commercial liquid fabric softeners on towels, as silicone residues coat natural loops and impair water absorbency. Add half a cup of distilled white vinegar to the final rinse cycle instead to break down detergent residues and restore loop fluffiness naturally."}
              </p>
            </div>
          </div>

          {/* Drying & Wrinkles */}
          <div className="care-rule-item">
            <div className="care-rule-icon">
              <Wind size={22} />
            </div>
            <div className="care-rule-content">
              <h4>
                {isRTL ? "التجفيف وسرعة الفرد" : "Drying & Wrinkle Prevention"}
              </h4>
              <p>
                {isRTL
                  ? "يُفضل التجفيف في الهواء الطلق أو في المجفف الآلي على درجة حرارة منخفضة، مع إخراج القطع فور انتهاء الدورة لمنع التجاعيد. بفضل مقاومة المايكروفايبر وخامة الوجهين للتجعد، ستخرج القطع ناعمة ومفرودة ولا تحتاج إلى كي على الإطلاق."
                  : "Tumble dry on low heat or line dry in a shaded, well-ventilated breeze; remove promptly to eliminate wrinkles. Thanks to the natural wrinkle resistance of microfiber and dual-face fabrics, your linens emerge smooth, soft, and require zero ironing."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <div className="care-cta-box">
        <div className="care-cta-text">
          <h3>
            {isRTL
              ? "هل لديك استفسار إضافي حول العناية بقطعتك؟"
              : "Have questions about caring for your pieces?"}
          </h3>
          <p>
            {isRTL
              ? "تصفح الأسئلة الشائعة أو تواصل مع كونسيرج تيري لاند عبر الواتساب للحصول على مساعدة فورية."
              : "Explore our FAQ or connect with the Terry Land concierge via WhatsApp for dedicated care advice."}
          </p>
        </div>
        <div className="care-cta-actions">
          <Link to="/faq" className="btn-primary">
            <span>{isRTL ? "الأسئلة الشائعة" : "View FAQ"}</span>
            {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
          </Link>
          <Link to="/shop" className="btn-outline">
            <ShoppingBag size={16} />
            <span>{isRTL ? "تسوق المجموعات" : "Shop Collections"}</span>
          </Link>
        </div>
      </div>
    </ContentPage>
  );
}
